import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";

import { Calendar, Download, PhoneCall, Signal, Sim } from "@/assets/icons/ds";
import { ButtonLink } from "@/components/Interface/Button/Button";
import { Notification } from "@/components/Interface/Notification/Notification";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { MetricTiles } from "@/components/Layout/MetricTiles/MetricTiles";
import { SubscriptionInfoBox } from "@/components/Layout/SubscriptionInfoBox/SubscriptionInfoBox";
import {
  useGetFlatPricesAndName,
  useIsMobileAbo,
} from "@/hooks/useDataContextHooks";
import { DataContext } from "@/provider/DataContextProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { ServiceIDs } from "@/utils/constants";
import { getEntry, hasBasketNumberPorting } from "@/utils/dataContextHelpers";
import { getTariffType, TariffTypes } from "@/utils/deviceUtils";
import { getProductSpeed } from "@/utils/productMatrix";
import {
  CheckoutTariffFeatureRow,
  getCheckoutTariffFeatureGroups,
  useHasPrechargedMobileAbo,
} from "@/utils/tariffUtils";

import { DeviceDetailsForm } from "./DeviceDetailsForm/DeviceDetailsForm";

interface Props {
  isFlatHome?: boolean;
  showErrors?: boolean;
  /** Customer has CreditClassC and needs a Swiss ID document to use roaming calls. */
  isInternationalCallsRestricted?: boolean;
}

const featureRowIcon: Record<CheckoutTariffFeatureRow["icon"], typeof Signal> =
  {
    signal: Signal,
    phone: PhoneCall,
  };

const TariffFeatureGroup: FC<{
  title: string;
  description?: string;
  rows: CheckoutTariffFeatureRow[];
}> = ({ title, description, rows }) => (
  <div className="flex w-full flex-col items-start gap-2 rounded-md bg-surface-quaternary p-4">
    <div className="flex w-full flex-col gap-1">
      <p className="text-body-sm font-semibold text-foreground-secondary">
        {title}
      </p>
      {description && (
        <p
          data-testid="international-restriction-hint"
          className="text-body-xs leading-none text-foreground-secondary"
        >
          {description}
        </p>
      )}
    </div>
    {rows.map((row) => {
      const Icon = featureRowIcon[row.icon];

      return (
        <div key={row.title} className="flex w-full items-center gap-2">
          <Icon className="size-6 shrink-0 text-foreground-primary" />
          <div className="flex flex-1 flex-col gap-1 leading-none">
            <p className="text-body-base font-semibold text-foreground-primary">
              {row.title}
            </p>
            <p className="text-body-xs text-foreground-secondary">
              {row.subtitle}
            </p>
          </div>
        </div>
      );
    })}
  </div>
);

/**
 * This component should implement the device assignment
 * and the rate picker during the onboarding.
 */
export const Summary: FC<Props> = ({
  isFlatHome,
  showErrors,
  isInternationalCallsRestricted,
}) => {
  const { dataContext } = useContext(DataContext);
  const { t } = useTranslation("translation", {
    keyPrefix: "Onboarding.sections.checkout.summary",
  });
  const { t: tUnscoped } = useTranslation();

  const entry = getEntry(dataContext);

  const { flatName, bandwidthLabel } = useGetFlatPricesAndName();
  const isMobileAbo = useIsMobileAbo();
  const hasPrechargedMobileAbo = useHasPrechargedMobileAbo(dataContext.basket);
  const isPrecharged =
    !!dataContext.selectedOption?.precharged || hasPrechargedMobileAbo;
  const isPurchaseIntent =
    dataContext.wizardEntry?.source === "purchase-intent";
  const serviceId = entry?.options?.[0]?.product?.id ?? entry?.product?.id;
  const tariffType = getTariffType(serviceId);
  const isDataOnlyTariff = tariffType === TariffTypes.dataTariff;
  const canChangeRate = !isFlatHome && !isPrecharged && !isPurchaseIntent;
  const hasONP = hasBasketNumberPorting(entry, dataContext.numberPorting);
  // The backend may have already persisted the porting number (mnpData) even
  // when the in-memory form state (dataContext.numberPorting) is gone, e.g.
  // after a reload landing straight back on checkout.
  const portedNumber =
    dataContext.numberPorting?.currentNumber ??
    entry?.telcoData?.mnpData?.msisdn;

  // Volume and minutes follow the service id (monthly or yearly product).
  const tariffFeatureGroups =
    !isFlatHome && !isDataOnlyTariff
      ? getCheckoutTariffFeatureGroups(tUnscoped, serviceId)
      : undefined;

  // Flat Home is sold with a fixed speed pair per generation - same numbers
  // `getSubscriptionSpeeds` uses post-purchase in "Your subscription"
  // (SubscriptionOptions.tsx), just read here from the product matrix instead
  // of a provisioned SIM. A data-only tariff instead states its download
  // speed directly (`bandwidthLabel`); upload is estimated as half of it,
  // the same approximation `getDataSpeedSummaryForRateSelection` and
  // `getSubscriptionSpeeds` already use for that gap.
  const dataOnlyDownload = isDataOnlyTariff ? Number(bandwidthLabel) : NaN;
  const speeds = isFlatHome
    ? getProductSpeed(serviceId)
    : isDataOnlyTariff &&
        !Number.isNaN(dataOnlyDownload) &&
        dataOnlyDownload > 0
      ? { download: dataOnlyDownload, upload: dataOnlyDownload / 2 }
      : undefined;
  const network = isFlatHome
    ? serviceId === ServiceIDs.FlatHome4G
      ? "4G"
      : "5G"
    : "5G";
  // Contract term is only shown for Flat Home (minimum term of 12 months).
  const durationLabel = isFlatHome
    ? tUnscoped("portal:flatHome.duration")
    : undefined;
  // Data-only tariffs fall back to the plain name (like a regular mobile abo)
  // if bandwidthLabel didn't resolve - Flat Home's speed pair is a fixed
  // catalog lookup by id, so it's never in that position.
  const showSpeedTiles = isFlatHome || (isDataOnlyTariff && !!speeds);

  return (
    <CardModule
      className="payment-summary"
      variant="white"
      bodyGap={6}
      title={tUnscoped("Common.checkout.summary")}
    >
      <DeviceDetailsForm showErrors={showErrors} />

      <div className="flex w-full flex-col gap-3">
        <div className="flex w-full items-center justify-between">
          <p className="text-body-base font-semibold leading-none text-foreground-primary">
            {tUnscoped("Common.tariff.options")}
          </p>
          {canChangeRate && (
            <ButtonLink
              data-testid="change-rate-button"
              variant="primary"
              size="sm"
              to={portalRoutes.addDevice.rateSelection}
            >
              {t("changeRate")}
            </ButtonLink>
          )}
        </div>
        {!showSpeedTiles && (
          <div className="flex w-full flex-col gap-2">
            <SubscriptionInfoBox className="flex items-center gap-3">
              <Sim className="size-6 shrink-0 text-foreground-primary" />
              <p className="text-body-lg font-semibold leading-md text-foreground-primary">
                {flatName}
              </p>
            </SubscriptionInfoBox>
            {tariffFeatureGroups && (
              <>
                <TariffFeatureGroup
                  title={tUnscoped("countries.CHE")}
                  rows={[tariffFeatureGroups.switzerland]}
                />
                {tariffFeatureGroups.international.length > 0 && (
                  <TariffFeatureGroup
                    title={tUnscoped("Common.tariff.international")}
                    description={
                      isInternationalCallsRestricted
                        ? t("internationalRestriction")
                        : undefined
                    }
                    rows={tariffFeatureGroups.international}
                  />
                )}
                {isMobileAbo && (
                  <Notification
                    dataTestid="number-porting-hint"
                    title={
                      hasONP
                        ? (portedNumber ?? t("portedNumberTitle"))
                        : t("newNumberTitle")
                    }
                    description={
                      hasONP ? t("portedNumberHint") : t("newNumberHint")
                    }
                  />
                )}
              </>
            )}
          </div>
        )}
        {showSpeedTiles && speeds && (
          <div className="flex w-full flex-col gap-2">
            <SubscriptionInfoBox className="flex items-center gap-3">
              <Sim className="size-6 shrink-0 text-foreground-primary" />
              <p className="text-body-lg font-semibold leading-md text-foreground-primary">
                {flatName}
              </p>
            </SubscriptionInfoBox>
            <MetricTiles
              metrics={[
                {
                  icon: <Download className="size-10" />,
                  value: `${speeds.download}`,
                  unit: tUnscoped("Common.tariff.mbitMax"),
                  label: tUnscoped(
                    "portal:cockpit.managementTile.subscription.options.download",
                  ),
                },
                {
                  icon: <Download className="size-10 rotate-180" />,
                  value: `${speeds.upload}`,
                  unit: tUnscoped("Common.tariff.mbitMax"),
                  label: tUnscoped(
                    "portal:cockpit.managementTile.subscription.options.upload",
                  ),
                },
                {
                  icon: <Signal className="size-6" />,
                  value: network,
                  unit: tUnscoped("Common.tariff.unlimited"),
                },
              ]}
            />
            {isFlatHome && durationLabel && (
              <SubscriptionInfoBox className="flex items-center gap-3">
                <Calendar className="size-6 shrink-0 text-foreground-primary" />
                <p className="text-body-lg font-semibold leading-md text-foreground-primary">
                  {durationLabel}
                </p>
              </SubscriptionInfoBox>
            )}
          </div>
        )}
      </div>
    </CardModule>
  );
};
